import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { Field } from 'formik';
import { Switch } from 'formik-mui';
import { getAmenityTypes, getAmenityTypeKeys } from '../../../../components/amenities/AmenityTypes';
import AmenityInput from './AmenityInput';
import {
  AMENITY_FORM_UNKNOWN,
  normalizeAmenityFormValues,
} from './amenityOverrideUtils';

function AmenitiesGrid({
  amenityTypes,
  amenities,
  updateAmenities,
  onAmenityChange,
}) {
  if (!updateAmenities) {
    return null;
  }

  return Array.from(amenityTypes.entries()).map(([amenityKey, amenityType]) => (
    <AmenityInput
      key={amenityKey}
      amenityKey={amenityKey}
      amenityType={amenityType}
      formValue={amenities?.[amenityKey] ?? AMENITY_FORM_UNKNOWN}
      onChange={onAmenityChange}
    />
  ));
}

AmenitiesGrid.propTypes = {
  amenityTypes: PropTypes.instanceOf(Map).isRequired,
  amenities: PropTypes.object,
  updateAmenities: PropTypes.bool.isRequired,
  onAmenityChange: PropTypes.func.isRequired,
};

function AmenitiesForm({
  values,
  setFieldValue,
}) {
  const amenityTypes = getAmenityTypes();
  const amenityKeys = getAmenityTypeKeys();
  const { updateAmenities, amenities = {} } = values;

  const handleAmenityChange = React.useCallback(
    (amenityKey, nextValue) => {
      setFieldValue(`amenities.${amenityKey}`, nextValue);
    },
    [setFieldValue],
  );

  React.useEffect(() => {
    if (!updateAmenities) {
      return;
    }

    const hasAllKeys = amenityKeys.every(
      (amenityKey) => Object.prototype.hasOwnProperty.call(amenities, amenityKey),
    );
    if (hasAllKeys) {
      return;
    }

    setFieldValue(
      'amenities',
      normalizeAmenityFormValues(amenities, amenityKeys),
    );
  }, [updateAmenities, amenities, amenityKeys, setFieldValue]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        p: 0,
        alignItems: 'flex-start',
      }}
    >
      <Grid item xs={12}>
        <Field
          component={Switch}
          type="checkbox"
          label="Update Amenities?"
          name="updateAmenities"
        />
        Update Amenities
      </Grid>
      <AmenitiesGrid
        amenityTypes={amenityTypes}
        amenities={amenities}
        updateAmenities={updateAmenities}
        onAmenityChange={handleAmenityChange}
      />
    </Grid>
  );
}

AmenitiesForm.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default AmenitiesForm;
